import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { useBackdropClickClose } from '../hooks/useBackdropClickClose';

export interface ConfirmDialogProps {
  isOpen: boolean;
  /** Header title (e.g. "Usunąć produkt?") */
  title: string;
  /** Main question / body (ReactNode so callers can bold names) */
  message: ReactNode;
  /** Optional muted subtext below the main message */
  description?: ReactNode;
  /** Header subtitle pod title. Default: "Tej operacji nie można cofnąć."
   * — nadpisz dla akcji które SĄ odwracalne (np. soft-delete do Kosza).
   * Ustaw null żeby kompletnie wyłączyć linię subtitle. */
  subtitle?: ReactNode | null;
  /** Primary action button label (e.g. "Usuń produkt") */
  confirmLabel: string;
  /** Secondary cancel label — defaults to "Anuluj" */
  cancelLabel?: string;
  /** Primary action handler */
  onConfirm: () => void;
  /** Close handler — triggered by Escape, backdrop click, or the cancel button */
  onClose: () => void;
  /** Disables buttons and shows a spinner on the confirm button while an async action runs */
  isPending?: boolean;
  /** Destructive styling (red button). Defaults to true since this is primarily a delete-confirm. */
  danger?: boolean;
}

/**
 * Shared confirm dialog używany wszędzie w miejsce window.confirm(). Stylistyka
 * zgodna z ui/Modal (branding 2026-04): bez ikony w kółku, tytuł h2 + opcjonalny
 * description. Ikona przycisku (X close) też jak w Modal.
 *
 * Konwencje (CLAUDE.md):
 *   - Closes on Escape
 *   - Closes on backdrop click
 *   - Primary button LEWO, Cancel PRAWO
 *
 * NEVER use `window.confirm()` — użyj tego komponentu.
 */
export function ConfirmDialog({
  isOpen,
  title,
  message,
  description,
  subtitle,
  confirmLabel,
  cancelLabel = 'Anuluj',
  onConfirm,
  onClose,
  isPending = false,
  danger = true,
}: ConfirmDialogProps) {
  // Default subtitle (zachowanie wsteczne): "Tej operacji nie można cofnąć."
  // `subtitle: null` świadomie wyłącza linię. Cokolwiek innego (string/ReactNode) nadpisuje.
  const resolvedSubtitle: ReactNode | null =
    subtitle === null ? null : subtitle === undefined ? 'Tej operacji nie można cofnąć.' : subtitle;
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, isPending, onClose]);

  const backdrop = useBackdropClickClose(onClose, !isPending);

  if (!isOpen) return null;

  const confirmButtonClass = danger
    ? 'btn-primary text-sm flex items-center gap-1.5 bg-danger hover:bg-danger border-danger'
    : 'btn-primary text-sm flex items-center gap-1.5';

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[20000] flex items-center justify-center p-4 bg-surface-base/90 backdrop-blur-sm animate-fade-in"
      {...backdrop}
    >
      <div
        className="relative w-full max-w-md bg-surface-raised border border-border rounded-card shadow-glass animate-scale-in flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - zgodny z ui/Modal (bez ikony w kółku) */}
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-border-subtle shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-text-primary leading-tight">{title}</h2>
            {resolvedSubtitle !== null && (
              <p className="text-xs text-text-muted mt-1.5 leading-relaxed">{resolvedSubtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => !isPending && onClose()}
            disabled={isPending}
            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-hover transition-colors disabled:opacity-40"
            aria-label="Zamknij"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-3 text-sm">
          <div>{message}</div>
          {description && <div className="text-xs text-text-muted">{description}</div>}
        </div>

        {/* Footer - Primary LEWO, Cancel PRAWO (konwencja) */}
        <div className="px-6 py-4 border-t border-border-subtle flex items-center gap-2">
          <button
            onClick={onConfirm}
            disabled={isPending}
            className={confirmButtonClass}
          >
            {isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
          <button
            onClick={onClose}
            disabled={isPending}
            className="btn-secondary text-sm ml-auto"
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
